import { captureException } from '@sentry/react';

type TryYourSiteDataLayerEvent = {
  event: 'try_your_site_url_submitted';
  tys_url_hostname: string;
  tys_page_path: string;
  tys_language: string;
  tys_timestamp: string;
};

function normalizeWebsiteUrl(value: string): string | null {
  // Normalize user input to a valid absolute URL for backend usage.
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;

  try {
    const withProtocol = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;
    const parsed = new URL(withProtocol);

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }

    const hostname = parsed.hostname.trim();
    const isLocalhost = hostname === 'localhost';
    const isIpv4 =
      /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname) &&
      hostname.split('.').every(function (part) {
        const num = Number(part);
        return Number.isInteger(num) && num >= 0 && num <= 255;
      });
    const hasValidDomainDots =
      hostname.includes('.') &&
      !hostname.startsWith('.') &&
      !hostname.endsWith('.');

    if (!isLocalhost && !isIpv4 && !hasValidDomainDots) {
      return null;
    }

    return parsed.toString();
  } catch {
    return null;
  }
}

function pushTryYourSiteUrlSubmitted(
  normalizedWebsiteUrl: string,
  language: string
): void {
  try {
    const hostname = new URL(normalizedWebsiteUrl).hostname;
    const dataLayerWindow = window as Window & {
      dataLayer?: TryYourSiteDataLayerEvent[];
    };
    dataLayerWindow.dataLayer = dataLayerWindow.dataLayer ?? [];
    dataLayerWindow.dataLayer.push({
      event: 'try_your_site_url_submitted',
      tys_url_hostname: hostname,
      tys_page_path: window.location.pathname,
      tys_language: language,
      tys_timestamp: new Date().toISOString(),
    });
  } catch (exception) {
    captureException(exception);
  }
}

function setStatus(
  statusElement: HTMLElement,
  message: string,
  isError: boolean
): void {
  statusElement.textContent = message;
  statusElement.classList.remove('hidden', 'text-success', 'text-danger');
  statusElement.classList.add(isError ? 'text-danger' : 'text-success');
}

function closeTryYourSiteModal(root: HTMLElement, modal: HTMLElement): void {
  // Close this modal instance and unlock body scroll if no modal is open.
  modal.classList.add('hidden');
  modal.classList.remove('flex');
  modal.setAttribute('aria-hidden', 'true');
  root.setAttribute('data-try-your-site-modal-open', 'false');

  if (
    !document.querySelector<HTMLElement>(
      '[data-try-your-site-modal-open="true"]'
    )
  ) {
    document.body.classList.remove('overflow-hidden');
  }
}

function openTryYourSiteModal(root: HTMLElement, modal: HTMLElement): void {
  modal.classList.remove('hidden');
  modal.classList.add('flex');
  modal.setAttribute('aria-hidden', 'false');
  root.setAttribute('data-try-your-site-modal-open', 'true');
  document.body.classList.add('overflow-hidden');
}

function initTryYourSite(root: HTMLElement): void {
  const urlForm = root.querySelector<HTMLFormElement>('[data-tys-url-form]');
  const websiteInput = root.querySelector<HTMLInputElement>(
    'input[name="website"]'
  );
  const urlError = root.querySelector<HTMLElement>('[data-tys-url-error]');
  const modal = root.querySelector<HTMLElement>('[data-tys-modal]');
  const form = root.querySelector<HTMLFormElement>('[data-tys-form]');
  const emailInput = root.querySelector<HTMLInputElement>(
    'input[name="email"]'
  );
  const statusElement = root.querySelector<HTMLElement>(
    '[data-tys-form-status]'
  );
  const submitButton =
    root.querySelector<HTMLButtonElement>('[data-tys-submit]');
  const fetchingStage = root.querySelector<HTMLElement>(
    '[data-tys-stage="fetching"]'
  );
  const trainingStage = root.querySelector<HTMLElement>(
    '[data-tys-stage="training"]'
  );
  const readyStage = root.querySelector<HTMLElement>(
    '[data-tys-stage="ready"]'
  );

  // Stop initialization when required markup is missing.
  if (
    !urlForm ||
    !websiteInput ||
    !urlError ||
    !modal ||
    !form ||
    !emailInput ||
    !statusElement ||
    !submitButton
  ) {
    return;
  }

  const submitLabel =
    submitButton.dataset.submitLabel ?? submitButton.textContent ?? 'Submit';
  const submittingLabel = root.dataset.tysSubmittingLabel ?? submitLabel;
  const errorGenericLabel =
    root.dataset.tysErrorGenericLabel ??
    'Something went wrong. Please try again.';
  const errorNetworkLabel =
    root.dataset.tysErrorNetworkLabel ??
    'Something went wrong while sending your request.';
  const language = root.dataset.language ?? 'en-US';
  let fetchingToTrainingTimer: number | null = null;
  let trainingToReadyTimer: number | null = null;

  // Clear stage transition timers to avoid duplicated transitions.
  function clearStageTimers(): void {
    if (fetchingToTrainingTimer !== null) {
      window.clearTimeout(fetchingToTrainingTimer);
      fetchingToTrainingTimer = null;
    }
    if (trainingToReadyTimer !== null) {
      window.clearTimeout(trainingToReadyTimer);
      trainingToReadyTimer = null;
    }
  }

  function showStage(stage: 'fetching' | 'training' | 'ready'): void {
    // Stage chips are optional, so guard before toggling classes.
    if (!fetchingStage || !trainingStage || !readyStage) return;

    fetchingStage.classList.toggle('hidden', stage !== 'fetching');
    fetchingStage.classList.toggle('flex', stage === 'fetching');
    trainingStage.classList.toggle('hidden', stage !== 'training');
    trainingStage.classList.toggle('flex', stage === 'training');
    readyStage.classList.toggle('hidden', stage !== 'ready');
    readyStage.classList.toggle('flex', stage === 'ready');
  }

  function resetStageFlow(): void {
    clearStageTimers();
    showStage('fetching');
  }

  function startStageFlow(): void {
    // Visual progress simulation while request/account setup is processing.
    resetStageFlow();
    fetchingToTrainingTimer = window.setTimeout(function () {
      showStage('training');
    }, 2000);
    trainingToReadyTimer = window.setTimeout(function () {
      showStage('ready');
    }, 7000);
  }

  function closeModalAndReset(): void {
    if (!modal) return;
    statusElement?.classList.add('hidden');
    closeTryYourSiteModal(root, modal);
    resetStageFlow();
  }

  websiteInput.addEventListener('blur', function () {
    const normalized = normalizeWebsiteUrl(websiteInput.value);
    if (normalized) {
      websiteInput.value = normalized;
      urlError.classList.add('hidden');
    }
  });

  urlForm.addEventListener('submit', function (event) {
    event.preventDefault();
    const normalized = normalizeWebsiteUrl(websiteInput.value);

    if (!normalized) {
      urlError.classList.remove('hidden');
      return;
    }

    urlError.classList.add('hidden');
    websiteInput.value = normalized;
    pushTryYourSiteUrlSubmitted(normalized, language);
    openTryYourSiteModal(root, modal);
    startStageFlow();
    emailInput.focus();
  });

  // Any close trigger should hide the modal and reset staged progress UI.
  root
    .querySelectorAll<HTMLButtonElement>('[data-tys-close-modal]')
    .forEach(function (button) {
      button.addEventListener('click', function () {
        closeModalAndReset();
      });
    });

  document.addEventListener('keydown', function (event) {
    if (
      event.key === 'Escape' &&
      root.dataset.tryYourSiteModalOpen === 'true'
    ) {
      closeModalAndReset();
    }
  });

  form.addEventListener('submit', async function (event) {
    event.preventDefault();
    submitButton.disabled = true;
    submitButton.textContent = submittingLabel;

    // API expects these fields for account creation flow.
    const email = String(new FormData(form).get('email') ?? '').trim();

    try {
      // Submit to local proxy route so server can forward headers/cookies upstream.
      const response = await fetch('/api/user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: email,
          email: email,
          companyName: email,
        }),
      });

      if (!response.ok) {
        let errorMessage = errorGenericLabel;
        try {
          const responseBody = (await response.json()) as { message?: string };
          if (responseBody.message) {
            errorMessage = responseBody.message;
          }
        } catch (exception) {
          captureException(exception);
          // Use the fallback message if response body cannot be parsed.
        }
        setStatus(statusElement, errorMessage, true);
        return;
      }

      form.reset();
      websiteInput.value = '';
      window.setTimeout(function () {
        const origin = new URL(window.location.origin);
        const loginUrl = new URL(`${origin}/login`);
        loginUrl.searchParams.set('email', email);
        window.location.href = loginUrl.toString();
      }, 500);
    } catch (exception) {
      captureException(exception);
      setStatus(statusElement, errorNetworkLabel, true);
    } finally {
      submitButton.disabled = false;
      submitButton.textContent = submitLabel;
    }
  });
}

const tryYourSiteRoot = document.querySelector<HTMLElement>(
  '[data-try-your-site]'
);
if (tryYourSiteRoot) {
  initTryYourSite(tryYourSiteRoot);
}
